import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

interface HotRecommendProps {
  movies: MediaItem[];
  tv: MediaItem[];
}

function RankList({
  title,
  icon,
  items,
  to,
}: {
  title: string;
  icon: string;
  items: MediaItem[];
  to: string;
}) {
  const [lead, ...rest] = items;
  if (!lead) return null;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-50 text-accent-500">
            <i className={icon}></i>
          </span>
          {title}
        </h3>
        <Link
          to={to}
          className="text-xs text-secondary-500 transition-colors hover:text-accent-600"
        >
          更多 <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <Link
          to={`/detail/${lead.id}`}
          className="group relative block w-full shrink-0 overflow-hidden rounded-lg sm:w-40"
        >
          <div className="relative aspect-[2/3] w-full">
            <img
              src={lead.poster}
              alt={`${lead.title} 海报`}
              title={`${lead.title} 热门推荐`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 to-transparent"></div>
            <div className="absolute bottom-3 left-3 right-3">
              <p className="truncate text-sm font-semibold text-white">
                {lead.title}
              </p>
              <p className="mt-0.5 text-xs text-white/70">{lead.status}</p>
            </div>
            <span className="absolute left-2 top-2 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-white">
              TOP1
            </span>
          </div>
        </Link>

        <ul className="flex flex-1 flex-col justify-between gap-2">
          {rest.slice(0, 5).map((item, i) => (
            <li key={item.id}>
              <Link
                to={`/detail/${item.id}`}
                className="group flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-background-100"
              >
                <span
                  className={`w-5 text-center font-heading text-sm font-bold ${
                    i < 2 ? "text-accent-500" : "text-secondary-400"
                  }`}
                >
                  {i + 2}
                </span>
                <span className="flex-1 truncate text-sm text-foreground-800 group-hover:text-accent-600">
                  {item.title}
                </span>
                <span className="shrink-0 text-xs text-secondary-400">
                  {item.region}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function HotRecommend({ movies, tv }: HotRecommendProps) {
  return (
    <section className="mx-auto mt-14 w-full px-4 md:px-6">
      <div className="mb-6">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-accent-500">
            <i className="ri-trophy-line"></i>
          </span>
          热门推荐
        </h2>
        <p className="mt-2 text-sm text-secondary-600">
          热门电影与电视剧专题合辑，看看大家都在追什么
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <RankList
          title="热门电影"
          icon="ri-film-line"
          items={movies}
          to="/category/movie?sort=hot"
        />
        <RankList
          title="热播电视剧"
          icon="ri-tv-2-line"
          items={tv}
          to="/category/tv?sort=hot"
        />
      </div>
    </section>
  );
}